import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppTrackSlider from "@toothfairy/shared-ui/AppTrackSlider";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import useApi from "@toothfairy/shared-api/useApi";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Platform, StyleSheet, View } from "react-native";
import envConfig from "../../../../envConfig";
import Files from "../../../API/Files";
import finetuningHelper from "../../../API/FineTuning/finetuningHelper";
import DownloadModalContent from "../../../Components/ToothFairyComponents/DownloadModalContent";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../../Components/ToothFairyComponents/ToothFairyModal";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// Curated IANA timezone list for the schedule dropdown (the guessed local
// zone is always included).
const TIMEZONES = [
  "UTC",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Adelaide",
  "Australia/Perth",
  "Australia/Darwin",
  "Australia/Hobart",
  "Pacific/Auckland",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Dubai",
  "Asia/Jerusalem",
  "Asia/Kolkata",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Lisbon",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Vienna",
  "Europe/Prague",
  "Europe/Warsaw",
  "Europe/Stockholm",
  "Europe/Zurich",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Sao_Paulo",
];

const isValidCron = (value) => {
  const expr = value.trim().toLowerCase();
  if (!expr) return false;
  const shortcuts = [
    "@hourly",
    "@daily",
    "@midnight",
    "@weekly",
    "@monthly",
    "@yearly",
    "@annually",
  ];
  if (shortcuts.includes(expr)) return true;
  const fields = expr.split(/\s+/);
  if (fields.length !== 5) return false;
  const rangeOf = (idx) =>
    [
      [0, 59],
      [0, 23],
      [1, 31],
      [1, 12],
      [0, 6],
    ][idx];
  const dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  return fields.every((field, idx) => {
    const [lo, hi] = rangeOf(idx);
    return field.split(",").every((token) => {
      let body = token;
      if (token.includes("/")) {
        const step = parseInt(token.split("/")[1], 10);
        if (!step || step < 1) return false;
        body = token.split("/")[0];
      }
      if (body === "*") return true;
      const parts = body.split("-");
      if (parts.length > 2) return false;
      const parse = (raw) =>
        /^\d+$/.test(raw)
          ? parseInt(raw, 10)
          : idx === 4
          ? dayNames.indexOf(raw)
          : NaN;
      if (parts.length === 2) {
        const a = parse(parts[0]);
        const b = parse(parts[1]);
        return !isNaN(a) && !isNaN(b) && a <= b && a >= lo && b <= hi;
      }
      const v = parse(body);
      return !isNaN(v) && v >= lo && v <= hi;
    });
  });
};

// Mirrors getTypeCode in ToothFairyOutput (private there, self-contained here)
// so the download modal receives the same training-type codes.
const getTypeCode = (type) => {
  if (type === "nlpTraining") return "nlp";
  else if (type === "readComprehensionTraining") return "rc";
  else if (type === "generativeTraining" || type === "ktoTraining") return "gn";
  else if (
    type === "conversationalTraining" ||
    type === "grpoTraining" ||
    type === "continuedPretraining"
  )
    return "cn";
  else if (type === "genAITraining") return "gn";
  return "cn";
};

// Mirrors getTypeFromTrainingType in useWorkspaces.
const getTypeFromTrainingType = (trainingType, trainingTypeCode = "gn") => {
  if (trainingType === "nlpTraining" || trainingType === "nlp") return "NLP";
  else if (
    trainingType === "readComprehensionTraining" ||
    trainingType === trainingTypeCode
  )
    return trainingTypeCode.toUpperCase();
  else if (trainingType === "generativeTraining" || trainingType === "gn")
    return "GN";
  else if (trainingType === "conversationalTraining" || trainingType === "cn")
    return "CN";
  else if (trainingType === "grpoTraining" || trainingType === "grpo")
    return "GRPO";
  else if (trainingType === "ktoTraining" || trainingType === "kto")
    return "KTO";
  else if (trainingType === "continuedPretraining" || trainingType === "cpt")
    return "CPT";
  else return "RC";
};

const FineTunedModelModal = ({
  trainingLog,
  token,
  handleDataDownload = () => {},
  isOpen,
  setIsOpen,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const theme = currentTheme?.theme;
  const styles = styleGenerator(currentTheme);
  const { getActiveWorkspace, getTrainingLogInfo } = useWorkspaces();

  const workspaceid = getActiveWorkspace()?.id;
  const trainingLogId = trainingLog?.id;
  const trainingType =
    trainingLog?.type || getTrainingLogInfo(trainingLogId)?.type;
  const downloadType = getTypeFromTrainingType(
    trainingType,
    getTypeCode(trainingType)
  );

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [serving, setServing] = useState(null);
  const [mode, setMode] = useState("on-demand");
  const [schedule, setSchedule] = useState({
    days: [...DAYS],
    start: "09:00",
    end: "17:00",
    timezone: dayjs.tz?.guess?.() || "UTC",
  });
  const [powerHours, setPowerHours] = useState(1);
  const [busy, setBusy] = useState(false);
  const [snapshotLabel, setSnapshotLabel] = useState("");
  const [rollbackVersion, setRollbackVersion] = useState("");
  const [cronInput, setCronInput] = useState("");
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selection, setSelection] = useState([]);

  const {
    data: filesResult,
    loading: filesInProgress,
    apiRequest: _filesRequest,
  } = useApi(Files.getDocumentsByLogId, envConfig);

  // Timezone dropdown: curated list plus the device's local zone when absent.
  const timezoneItems = useMemo(() => {
    const toItems = (zones) =>
      zones.filter(Boolean).map((z) => ({ name: z, id: z }));
    const guessed = dayjs.tz?.guess?.();
    const zoneList =
      guessed && !TIMEZONES.includes(guessed)
        ? [guessed, ...TIMEZONES]
        : TIMEZONES;
    return toItems(zoneList);
  }, []);

  const load = async () => {
    if (!trainingLogId || !token) return;
    setLoading(true);
    try {
      const [statusRes, servingRes] = await Promise.all([
        finetuningHelper
          .getTrainingStatus({ token, trainingLogId })
          .catch(() => null),
        finetuningHelper.getServing({ token, trainingLogId }).catch(() => null),
      ]);
      setStatus(statusRes);
      if (servingRes) {
        setServing(servingRes);
        setMode(servingRes.mode || "on-demand");
        if (servingRes.schedule) {
          // Cron-only schedules have no days/start/end — fall back to the
          // defaults (all days selected) instead of undefined fields.
          setSchedule((prev) => ({
            ...prev,
            ...servingRes.schedule,
            days: servingRes.schedule.days?.length
              ? servingRes.schedule.days
              : [...DAYS],
            start: servingRes.schedule.start || prev.start,
            end: servingRes.schedule.end || prev.end,
          }));
          setCronInput(servingRes.schedule.cron || "");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, trainingLogId, token]);

  const openUrl = (url) => {
    if (url) window.open(url, "_blank");
  };

  const handleDownloadOpen = async () => {
    setIsDownloadModalOpen(true);
    setSelection([]);
    if (workspaceid && trainingLogId) {
      // Same listing call as ToothFairyOutput (training context)
      await _filesRequest(
        workspaceid,
        trainingLogId,
        downloadType.toLowerCase(),
        "training"
      );
    }
  };

  const handleDownloadFormSubmit = () => {
    handleDataDownload({
      workspaceid,
      logId: trainingLogId,
      type: downloadType.toLowerCase(),
      selection,
    });
  };

  const handleModeChange = async (nextMode) => {
    setMode(nextMode);
    setBusy(true);
    try {
      const res = await finetuningHelper.setServing({
        token,
        trainingLogId,
        mode: nextMode,
        schedule: nextMode === "scheduled" ? schedule : undefined,
      });
      setServing(res || serving);
      AppToasty.show(t("training.serving.scheduleSaved"), { type: "success" });
    } catch (e) {
      AppToasty.show(e?.message || t("training.serving.scheduleError"), {
        type: "danger",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSchedule = async () => {
    const cronExpr = cronInput.trim();
    if (cronExpr && !isValidCron(cronExpr)) {
      AppToasty.show(t("training.serving.cronInvalid"), { type: "danger" });
      return;
    }
    if (!cronExpr && schedule.start >= schedule.end) {
      AppToasty.show(t("training.serving.scheduleError"), { type: "danger" });
      return;
    }
    setBusy(true);
    try {
      const res = await finetuningHelper.setServing({
        token,
        trainingLogId,
        mode: "scheduled",
        schedule: {
          ...schedule,
          cron: cronExpr || undefined,
          holdHours: undefined,
        },
      });
      setServing(res || serving);
      AppToasty.show(t("training.serving.scheduleSaved"), { type: "success" });
    } catch (e) {
      AppToasty.show(e?.message || t("training.serving.scheduleError"), {
        type: "danger",
      });
    } finally {
      setBusy(false);
    }
  };

  const handlePower = async (state) => {
    setBusy(true);
    try {
      const res = await finetuningHelper.setServingPower({
        token,
        trainingLogId,
        state,
        hours: state === "on" ? powerHours || 1 : undefined,
      });
      setServing(res || serving);
      AppToasty.show(t("training.serving.powerUpdated"), { type: "success" });
    } catch (e) {
      AppToasty.show(e?.message || t("training.serving.powerError"), {
        type: "danger",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSnapshot = async () => {
    setBusy(true);
    try {
      await finetuningHelper.snapshotAdapterVersion({
        token,
        trainingLogId,
        label: snapshotLabel.trim() || undefined,
      });
      setSnapshotLabel("");
      AppToasty.show(t("training.adapters.snapshotSaved"), { type: "success" });
    } catch (e) {
      AppToasty.show(e?.message || t("training.adapters.snapshotError"), {
        type: "danger",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRollback = async () => {
    setBusy(true);
    try {
      const res = await finetuningHelper.rollbackAdapter({
        token,
        trainingLogId,
        version: rollbackVersion.trim()
          ? parseInt(rollbackVersion, 10)
          : undefined,
      });
      const urls = res?.downloads || res?.urls || [];
      (Array.isArray(urls) ? urls : Object.values(urls)).forEach(openUrl);
      AppToasty.show(t("training.adapters.rollbackDone"), { type: "success" });
    } catch (e) {
      AppToasty.show(e?.message || t("training.adapters.rollbackError"), {
        type: "danger",
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleDay = (day) => {
    setSchedule((prev) => {
      const days = [...(prev.days || [])];
      if (days.includes(day)) {
        // keep at least one day selected so the schedule is never empty
        if (days.length === 1) return prev;
        return { ...prev, days: days.filter((d) => d !== day) };
      }
      return { ...prev, days: [...days, day] };
    });
  };

  const lane = serving?.lane || {};

  return (
    <ToothFairyModal
      isModalVisible={isOpen}
      isCloseButtonVisible
      modalTitle={t("training.fineTunedModel.title", "Fine-tuned model")}
      closeModal={() => setIsOpen(false)}
      modalStyle={
        Platform.OS === "web"
          ? {
              maxWidth: 680,
              width: "95%",
              maxHeight: "90%",
              height: "fit-content",
              margin: "auto",
              borderRadius: 20,
            }
          : {}
      }
      modalInnerContentStyle={{ paddingHorizontal: 30, paddingVertical: 10 }}
    >
      {loading ? (
        <View style={styles.center}>
          <AppText style={{ color: theme?.mediumGrey }}>
            {t("training.downloads.fetching")}
          </AppText>
        </View>
      ) : (
        <View style={styles.body}>
          {/* Summary */}
          <View style={styles.summary}>
            <AppText style={styles.name} color={theme?.font_color}>
              {trainingLog?.name || status?.name || trainingLogId}
            </AppText>
            {serving?.modelId && (
              <AppText style={styles.mono} color={theme?.mediumGrey}>
                {serving.modelId}
              </AppText>
            )}
            {(status?.base_model || status?.compute_uoi != null) && (
              <AppText style={styles.metaText} color={theme?.mediumGrey}>
                {status?.base_model
                  ? `${t("training.fineTunedModel.baseModel")}: ${
                      status.base_model
                    }`
                  : ""}
                {status?.base_model && status?.compute_uoi != null ? "   " : ""}
                {status?.compute_uoi != null
                  ? `${t("training.fineTunedModel.computeUoi")}: ${
                      status.compute_uoi
                    }`
                  : ""}
              </AppText>
            )}
          </View>

          {/* Serving */}
          <AppExpander
            title={t("training.serving.title")}
            icon={AppAssets.icons.hosting}
            isExpandedOnLoad={true}
            style={styles.section}
          >
            <View style={styles.sectionBody}>
              <View style={styles.row}>
                <View
                  style={[
                    styles.dot,
                    {
                      backgroundColor:
                        lane?.status === "ready" ? "#2A9D8F" : "#888888",
                    },
                  ]}
                />
                <AppText style={styles.laneText} color={theme?.font_color}>
                  {t("training.serving.laneStatus")}:{" "}
                  {lane?.status || "stopped"}
                  {lane?.port ? ` · port ${lane.port}` : ""}
                </AppText>
              </View>
              <AppText style={styles.hint} color={theme?.mediumGrey}>
                {t("training.serving.laneHint")}
              </AppText>
              {serving?.warm_seconds_accrued != null && (
                <AppText style={styles.hint} color={theme?.mediumGrey}>
                  {t("training.serving.warmHours")}:{" "}
                  {(serving.warm_seconds_accrued / 3600).toFixed(1)}
                </AppText>
              )}
              <AppDropDown
                showTextInputLabel
                isElevated={false}
                label={t("training.serving.mode")}
                items={[
                  { id: "on-demand", name: t("training.serving.modeOnDemand") },
                  {
                    id: "scheduled",
                    name: t("training.serving.modeScheduled"),
                  },
                  { id: "always", name: t("training.serving.modeAlways") },
                ]}
                propertyLabel="name"
                propertyValue="id"
                selectedValue={mode}
                onValueChange={handleModeChange}
                inputLabelStyle={{
                  marginLeft: 10,
                }}
                dropDownStyle={{
                  borderColor: theme?.border_color,
                  maxWidth: 300,
                  marginLeft: 10,
                }}
              />
              <AppText style={styles.hint} color={theme?.mediumGrey}>
                {mode === "on-demand"
                  ? t("training.serving.modeOnDemandDesc")
                  : mode === "scheduled"
                  ? t("training.serving.modeScheduledDesc")
                  : t("training.serving.modeAlwaysDesc")}
              </AppText>
            </View>
          </AppExpander>

          {/* Schedule */}
          {mode === "scheduled" && (
            <AppExpander
              title={t("training.serving.schedule")}
              icon={AppAssets.icons.days}
              isExpandedOnLoad={true}
              style={styles.section}
            >
              <View style={styles.sectionBody}>
                <View style={styles.dayRow}>
                  {DAYS.map((d) => (
                    <ToothFairyButton
                      key={d}
                      title={d}
                      onPress={() => toggleDay(d)}
                      fontColor={
                        (schedule.days || []).includes(d)
                          ? theme?.white
                          : theme?.font_color
                      }
                      backgroundColor={
                        (schedule.days || []).includes(d)
                          ? theme?.primary
                          : theme?.transparent
                      }
                      wrapperStyle={styles.dayBtn}
                    />
                  ))}
                </View>
                <View style={styles.timeRow}>
                  <AppTextInput
                    showTextInputLabel
                    label={t("training.serving.start")}
                    value={schedule.start}
                    onChangeText={(v) =>
                      setSchedule((p) => ({ ...p, start: v }))
                    }
                    width={120}
                    placeholder="HH:MM"
                    style={{
                      width: "-webkit-fill-available",
                    }}
                  />
                  <AppTextInput
                    showTextInputLabel
                    label={t("training.serving.end")}
                    value={schedule.end}
                    onChangeText={(v) => setSchedule((p) => ({ ...p, end: v }))}
                    width={120}
                    placeholder="HH:MM"
                    style={{
                      width: "-webkit-fill-available",
                    }}
                  />
                </View>
                <AppDropDown
                  showTextInputLabel
                  isElevated={false}
                  label={t("training.serving.timezone")}
                  items={timezoneItems}
                  propertyLabel="name"
                  propertyValue="id"
                  selectedValue={schedule.timezone}
                  onValueChange={(v) =>
                    setSchedule((p) => ({ ...p, timezone: v }))
                  }
                  dropDownStyle={{
                    borderColor: theme?.border_color,
                    maxWidth: 320,
                    marginLeft: 5,
                  }}
                  inputLabelStyle={{
                    marginLeft: 10,
                  }}
                />
                <AppTextInput
                  textInputLabelStyle={{
                    marginLeft: 10,
                    marginTop: 10,
                    marginBottom: 5,
                  }}
                  showTextInputLabel
                  label={t("training.serving.cron")}
                  value={cronInput}
                  onChangeText={setCronInput}
                  width="100%"
                  placeholder="0 7 * * 1-5"
                />
                <AppText style={styles.hint} color={theme?.mediumGrey}>
                  {cronInput.trim()
                    ? t("training.serving.cronDesc")
                    : t("training.serving.scheduleDesc")}
                </AppText>
                <ToothFairyButton
                  title={t("training.serving.saveSchedule")}
                  onPress={handleSaveSchedule}
                  disabled={busy}
                  wrapperStyle={{
                    marginTop: 12,
                    minWidth: 150,
                    height: 40,
                    alignSelf: "flex-start",
                  }}
                />
              </View>
            </AppExpander>
          )}

          {/* Power */}
          <AppExpander
            title={t("training.serving.warmNow")}
            icon={AppAssets.icons.bolt}
            style={styles.section}
          >
            <View style={styles.sectionBody}>
              <AppText style={styles.hint} color={theme?.mediumGrey}>
                {t("training.serving.warmDesc")}
              </AppText>
              <AppTrackSlider
                value={powerHours}
                onValueChange={(v) => setPowerHours(v)}
                minimumValue={1}
                maximumValue={23}
                step={1}
                wrapperStyle={{
                  marginTop: 8,
                  width: "auto",
                  marginRight: 8,
                  marginLeft: 14,
                }}
                style={{ width: "100%" }}
              />
              <View style={styles.buttonRow}>
                <ToothFairyButton
                  title={t("training.serving.warmNow")}
                  onPress={() => handlePower("on")}
                  disabled={busy}
                  wrapperStyle={{ minWidth: 120, height: 40 }}
                />
                <ToothFairyButton
                  title={t("training.serving.release")}
                  onPress={() => handlePower("off")}
                  disabled={busy}
                  fontColor="#E76F51"
                  backgroundColor={theme?.transparent}
                  wrapperStyle={{ minWidth: 110, height: 40 }}
                />
              </View>
            </View>
          </AppExpander>

          {/* Adapter versions */}
          <AppExpander
            title={t("training.adapters.title")}
            icon={AppAssets.icons.credentials}
            style={styles.section}
          >
            <View style={styles.sectionBody}>
              <AppText style={styles.hint} color={theme?.mediumGrey}>
                {t("training.adapters.snapshotDesc")}
              </AppText>
              <AppTextInput
                showTextInputLabel
                label={t("training.adapters.snapshotLabel")}
                value={snapshotLabel}
                onChangeText={setSnapshotLabel}
                width="100%"
                placeholder="training.adapters.snapshotLabelPlaceholder"
              />
              <ToothFairyButton
                title={t("training.adapters.snapshot")}
                onPress={handleSnapshot}
                disabled={busy}
                wrapperStyle={{
                  marginTop: 12,
                  minWidth: 130,
                  height: 40,
                  alignSelf: "flex-start",
                }}
              />
              <AppText style={styles.hint} color={theme?.mediumGrey}>
                {t("training.adapters.rollbackDesc")}
              </AppText>
              <AppTextInput
                showTextInputLabel
                label={t("training.adapters.versionNumber")}
                value={rollbackVersion}
                onChangeText={setRollbackVersion}
                width="100%"
                placeholder="training.adapters.versionPlaceholder"
              />
              <ToothFairyButton
                title={t("training.adapters.rollback")}
                onPress={handleRollback}
                disabled={busy}
                fontColor={theme?.white}
                backgroundColor={"#E76F51"}
                wrapperStyle={{
                  marginTop: 12,
                  minWidth: 130,
                  height: 40,
                  alignSelf: "flex-start",
                }}
              />
            </View>
          </AppExpander>

          {/* Downloads */}
          <View
            style={[
              styles.section,
              { marginLeft: 9, marginTop: 10, alignItems: "flex-start" },
            ]}
          >
            <View style={styles.sectionHeader}>
              <AppIcon
                icon={AppAssets.icons.data}
                color={theme?.font_color}
                size={14}
              />
              <AppText style={styles.sectionTitle} color={theme?.font_color}>
                {t("training.downloads.checkpoints")}
              </AppText>
            </View>
            <View style={styles.sectionBody}>
              <AppText style={styles.hint} color={theme?.mediumGrey}>
                {t("training.downloads.desc")}
              </AppText>
              <ToothFairyButton
                title={t("downloadModal.download")}
                onPress={handleDownloadOpen}
                wrapperStyle={{
                  minWidth: 130,
                  height: 40,
                  alignSelf: "flex-start",
                }}
              />
            </View>
          </View>
        </View>
      )}

      {/* Nested download modal — same experience as the training details view */}
      <ToothFairyModal
        isModalVisible={isDownloadModalOpen}
        isCloseButtonVisible
        modalTitle={t("training.downloads.checkpoints")}
        closeModal={() => setIsDownloadModalOpen(false)}
        modalStyle={
          Platform.OS === "web"
            ? {
                width: "95%",
                maxWidth: 600,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 16,
                maxHeight: "80%",
              }
            : {}
        }
        modalInnerContentStyle={{ paddingHorizontal: 20, paddingVertical: 16 }}
      >
        {filesInProgress ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color={theme?.primary} />
          </View>
        ) : (
          <DownloadModalContent
            context="training"
            filesResult={filesResult}
            selection={selection}
            onSelectionChange={(v) => setSelection([...v])}
            type={downloadType}
            handleFormSubmit={handleDownloadFormSubmit}
          />
        )}
      </ToothFairyModal>
    </ToothFairyModal>
  );
};

const styleGenerator = (currentTheme) => {
  const theme = currentTheme?.theme;
  return StyleSheet.create({
    center: {
      alignItems: "center",
      justifyContent: "center",
      padding: 40,
    },
    body: {
      width: "100%",
      maxWidth: 600,
      alignSelf: "center",
      gap: 14,
      paddingBottom: 10,
    },
    section: { width: "100%", alignItems: "center" },
    sectionBody: { width: "100%", paddingLeft: 2 },
    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    sectionTitle: { fontFamily: "boldFont", fontSize: 14, marginTop: 0 },
    summary: { marginBottom: 4 },
    name: { fontSize: 18, fontWeight: "700", marginBottom: 4 },
    laneText: { fontSize: 13, marginLeft: 10 },
    hint: {
      fontSize: 12,
      marginTop: 5,
      marginBottom: 8,
      textAlign: "left",
      lineHeight: 17,
      marginLeft: 10,
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
    },
    buttonRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      marginTop: 12,
    },
    dayRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 6,
      marginTop: 10,
      marginBottom: 10,
      width: "100%",
      marginLeft: -7,
    },
    dayBtn: { minWidth: 52, height: 32, paddingHorizontal: 0 },
    timeRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 12,
      marginBottom: 6,
    },
  });
};

export default FineTunedModelModal;
